import { styled, ButtonBase, Typography } from '@mui/material';
import { Header } from 'components/Header';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { RouteName } from 'stores/RouteStore';

const NavLink = styled(ButtonBase)(({ theme }) => ({
  width: '100%',
  textAlign: 'left',
  padding: '5px 12px',
  display: 'block',
  borderRadius: 4,
  '&.selected': {
    background: 'rgba(66, 96, 211, 0.08)',
    borderRadius: '4px',
    color: theme.palette.primary.main,
    '& p': {
      fontWeight: 700,
    },
  },
}));

export type Link = {
  route: RouteName;
  otherRoutes?: RouteName[];
  text: string;
};

type RenderBodyProps = {
  scrollToTop: () => void;
};

type NavigationPageProps = {
  links?: Link[];
  route?: RouteName;
  navigateByName?: (route: RouteName, data?: Object) => void;
  onBack?: () => void;
  renderBody: ({ scrollToTop }: RenderBodyProps) => JSX.Element;
};

const NavigationPage = observer(({ onBack, links = [], route, navigateByName, renderBody }: NavigationPageProps) => {
  const scroller = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    scroller.current?.scrollTo(0, 0);
  };

  const setRouteAndScrollToTop = (route: RouteName, data?: Object) => {
    navigateByName?.(route, data);
    scrollToTop();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header onBack={onBack} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
        }}
      >
        <div
          aria-label="sidebar"
          style={{
            width: 260,
            padding: '16px 14px',
            borderRight: '1px solid rgba(215, 222, 233, 1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {links.map((link) => (
            <NavLink
              key={link.route}
              onClick={() => setRouteAndScrollToTop(link.route)}
              className={
                link.route === route || (route && link.otherRoutes?.includes(route)) ? 'selected' : 'unselected'
              }
            >
              <Typography variant="body2">{link.text}</Typography>
            </NavLink>
          ))}
        </div>
        <div
          ref={scroller}
          aria-label="main"
          style={{
            flex: 1,
            marginLeft: 32,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {renderBody({ scrollToTop })}
        </div>
      </div>
    </div>
  );
});

export { NavigationPage };
